import PropTypes from 'prop-types';
import { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import html2pdf from 'html2pdf.js';
import { Box, Dialog, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import SendReportSection from 'pages/admit/SendReportSection';
import { QUILL_PRINT_CONTENT_CSS } from 'components/QuillEditor/quillContentStyles';

export default function InvoicePrintPreviewModal({
  open,
  onClose,
  children,
  title,
  reportId,
  patientId,
  patientName,
  isThermalPrint = false,
  isA5Print = false,
  autoSendWhatsAppKey,
  onAutoSendComplete
}) {
  const componentRef = useRef(null);
  const pageStyle = isThermalPrint
    ? `@page { size: 72mm auto; margin: 0; } body { margin: 0; font-family: Arial, sans-serif; font-size: 9px; } ${QUILL_PRINT_CONTENT_CSS}`
    : `${isA5Print ? '@page { size: A5; margin: 8mm; }' : ''} ${QUILL_PRINT_CONTENT_CSS}`;

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: title || 'Invoice',
    pageStyle
  });

  const generatePdfBlob = async () => {
    const element = componentRef.current;
    const heightMm = Math.max(60, Math.ceil((element.scrollHeight * 25.4) / 96) + 8);
    const format = isThermalPrint ? [72, heightMm] : (isA5Print ? 'a5' : 'a4');

    return html2pdf()
      .set({
        margin: 0,
        filename: `${title || 'Invoice'}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true },
        jsPDF: { unit: 'mm', format, orientation: 'portrait' }
      })
      .from(element)
      .outputPdf('blob');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: 1,
          p: 1.5,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Typography variant="h6">Invoice Preview</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <SendReportSection
            reportId={reportId}
            generatePdfBlob={generatePdfBlob}
            patientId={patientId}
            patientName={patientName}
            autoSendWhatsAppKey={autoSendWhatsAppKey}
            onAutoSendComplete={onAutoSendComplete}
          />
          <Tooltip title="Print invoice">
            <IconButton onClick={handlePrint} aria-label="Print invoice">
              <PrintIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close preview">
            <IconButton onClick={onClose} aria-label="Close preview">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={{ overflow: 'auto', bgcolor: 'grey.100', p: { xs: 1, sm: 3 } }}>
        <Box
          ref={componentRef}
          sx={{
            width: isThermalPrint ? '72mm' : (isA5Print ? '148mm' : '210mm'),
            maxWidth: '100%',
            minHeight: isThermalPrint ? 'auto' : (isA5Print ? '210mm' : '297mm'),
            mx: 'auto',
            bgcolor: 'background.paper',
            fontSize: isThermalPrint ? '9px' : 'inherit',
            '@media print': {
              width: isThermalPrint ? '72mm' : '100%',
              minHeight: 0,
              p: 0,
              m: 0
            },
            ...(isThermalPrint && {
              '& .MuiTableCell-root': { p: '2px', fontSize: '9px' },
              '&, & *': { breakInside: 'avoid-page', pageBreakInside: 'avoid' }
            })
          }}
        >
          {children}
        </Box>
      </Box>
    </Dialog>
  );
}

InvoicePrintPreviewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  reportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  patientName: PropTypes.string,
  isThermalPrint: PropTypes.bool,
  isA5Print: PropTypes.bool,
  autoSendWhatsAppKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onAutoSendComplete: PropTypes.func
};